/**
 * Generated by orval 🍺
 * Do not edit manually.
 * SolidOps API
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  MembershipPageResponseOutput,
  MembershipResponseOutput,
  TeamPageResponseOutput,
  TeamResponseOutput,
} from '../model';
import type {
  ListTeamMembershipsParams,
  ListTeamsParams,
  MembershipCreateBody,
  MembershipEndBody,
  TeamCreateBody,
  TeamUpdateBody,
} from '../model';

import { validatedFetch } from '../../client';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getListTeamsUrl = (params?: ListTeamsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/teams?${stringifiedParams}` : `/api/v1/teams`;
};

export const listTeams = async (
  params?: ListTeamsParams,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<TeamPageResponseOutput> => {
  return validatedFetch<TeamPageResponseOutput>(getListTeamsUrl(params), {
    ...options,
    method: 'GET',

    schema: TeamPageResponseOutput,
  });
};

export const getListTeamsQueryKey = (params?: ListTeamsParams) => {
  return [`/api/v1/teams`, ...(params ? [params] : [])] as const;
};

export const getListTeamsQueryOptions = <
  TData = Awaited<ReturnType<typeof listTeams>>,
  TError = unknown,
>(
  params?: ListTeamsParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listTeams>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListTeamsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listTeams>>> = ({ signal }) =>
    listTeams(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listTeams>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListTeamsQueryResult = NonNullable<Awaited<ReturnType<typeof listTeams>>>;
export type ListTeamsQueryError = unknown;

export function useListTeams<TData = Awaited<ReturnType<typeof listTeams>>, TError = unknown>(
  params?: ListTeamsParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listTeams>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListTeamsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getCreateTeamUrl = () => {
  return `/api/v1/teams`;
};

export const createTeam = async (
  teamCreateBody: TeamCreateBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<TeamResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<TeamResponseOutput>(getCreateTeamUrl(), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(teamCreateBody),
    schema: TeamResponseOutput,
  });
};

export const getCreateTeamMutationKey = () => ['createTeam'] as const;

export const getCreateTeamMutationOptions = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createTeam>>,
    TError,
    CreateTeamMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createTeam>>,
  TError,
  CreateTeamMutationVariables,
  TContext
> => {
  const mutationKey = getCreateTeamMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createTeam>>,
    CreateTeamMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return createTeam(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateTeamMutationResult = NonNullable<Awaited<ReturnType<typeof createTeam>>>;
export type CreateTeamMutationBody = TeamCreateBody;
export type CreateTeamMutationError = unknown;
export type CreateTeamMutationVariables = { data: TeamCreateBody };

export const useCreateTeam = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createTeam>>,
    TError,
    CreateTeamMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createTeam>>,
  TError,
  CreateTeamMutationVariables,
  TContext
> => {
  return useMutation(getCreateTeamMutationOptions(options));
};
export const getGetTeamUrl = (teamId: string) => {
  return `/api/v1/teams/${teamId}`;
};

export const getTeam = async (
  teamId: string,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<TeamResponseOutput> => {
  return validatedFetch<TeamResponseOutput>(getGetTeamUrl(teamId), {
    ...options,
    method: 'GET',

    schema: TeamResponseOutput,
  });
};

export const getGetTeamQueryKey = (teamId: string) => {
  return [`/api/v1/teams/${teamId}`] as const;
};

export const getGetTeamQueryOptions = <
  TData = Awaited<ReturnType<typeof getTeam>>,
  TError = unknown,
>(
  teamId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getTeam>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTeamQueryKey(teamId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTeam>>> = ({ signal }) =>
    getTeam(teamId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: teamId !== null && teamId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getTeam>>, TError, TData> & { queryKey: QueryKey };
};

export type GetTeamQueryResult = NonNullable<Awaited<ReturnType<typeof getTeam>>>;
export type GetTeamQueryError = unknown;

export function useGetTeam<TData = Awaited<ReturnType<typeof getTeam>>, TError = unknown>(
  teamId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getTeam>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetTeamQueryOptions(teamId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getUpdateTeamUrl = (teamId: string) => {
  return `/api/v1/teams/${teamId}`;
};

export const updateTeam = async (
  teamId: string,
  teamUpdateBody: TeamUpdateBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<TeamResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<TeamResponseOutput>(getUpdateTeamUrl(teamId), {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(teamUpdateBody),
    schema: TeamResponseOutput,
  });
};

export const getUpdateTeamMutationKey = () => ['updateTeam'] as const;

export const getUpdateTeamMutationOptions = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateTeam>>,
    TError,
    UpdateTeamMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateTeam>>,
  TError,
  UpdateTeamMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateTeamMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateTeam>>,
    UpdateTeamMutationVariables
  > = (props) => {
    const { teamId, data } = props ?? {};

    return updateTeam(teamId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateTeamMutationResult = NonNullable<Awaited<ReturnType<typeof updateTeam>>>;
export type UpdateTeamMutationBody = TeamUpdateBody;
export type UpdateTeamMutationError = unknown;
export type UpdateTeamMutationVariables = { teamId: string; data: TeamUpdateBody };

export const useUpdateTeam = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateTeam>>,
    TError,
    UpdateTeamMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateTeam>>,
  TError,
  UpdateTeamMutationVariables,
  TContext
> => {
  return useMutation(getUpdateTeamMutationOptions(options));
};
export const getListTeamMembershipsUrl = (teamId: string, params?: ListTeamMembershipsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/teams/${teamId}/memberships?${stringifiedParams}`
    : `/api/v1/teams/${teamId}/memberships`;
};

export const listTeamMemberships = async (
  teamId: string,
  params?: ListTeamMembershipsParams,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<MembershipPageResponseOutput> => {
  return validatedFetch<MembershipPageResponseOutput>(getListTeamMembershipsUrl(teamId, params), {
    ...options,
    method: 'GET',

    schema: MembershipPageResponseOutput,
  });
};

export const getListTeamMembershipsQueryKey = (
  teamId: string,
  params?: ListTeamMembershipsParams,
) => {
  return [`/api/v1/teams/${teamId}/memberships`, ...(params ? [params] : [])] as const;
};

export const getListTeamMembershipsQueryOptions = <
  TData = Awaited<ReturnType<typeof listTeamMemberships>>,
  TError = unknown,
>(
  teamId: string,
  params?: ListTeamMembershipsParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listTeamMemberships>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListTeamMembershipsQueryKey(teamId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listTeamMemberships>>> = ({ signal }) =>
    listTeamMemberships(teamId, params, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: teamId !== null && teamId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listTeamMemberships>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type ListTeamMembershipsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listTeamMemberships>>
>;
export type ListTeamMembershipsQueryError = unknown;

export function useListTeamMemberships<
  TData = Awaited<ReturnType<typeof listTeamMemberships>>,
  TError = unknown,
>(
  teamId: string,
  params?: ListTeamMembershipsParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listTeamMemberships>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListTeamMembershipsQueryOptions(teamId, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getCreateTeamMembershipUrl = (teamId: string) => {
  return `/api/v1/teams/${teamId}/memberships`;
};

export const createTeamMembership = async (
  teamId: string,
  membershipCreateBody: MembershipCreateBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<MembershipResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<MembershipResponseOutput>(getCreateTeamMembershipUrl(teamId), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(membershipCreateBody),
    schema: MembershipResponseOutput,
  });
};

export const getCreateTeamMembershipMutationKey = () => ['createTeamMembership'] as const;

export const getCreateTeamMembershipMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createTeamMembership>>,
    TError,
    CreateTeamMembershipMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createTeamMembership>>,
  TError,
  CreateTeamMembershipMutationVariables,
  TContext
> => {
  const mutationKey = getCreateTeamMembershipMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createTeamMembership>>,
    CreateTeamMembershipMutationVariables
  > = (props) => {
    const { teamId, data } = props ?? {};

    return createTeamMembership(teamId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateTeamMembershipMutationResult = NonNullable<
  Awaited<ReturnType<typeof createTeamMembership>>
>;
export type CreateTeamMembershipMutationBody = MembershipCreateBody;
export type CreateTeamMembershipMutationError = unknown;
export type CreateTeamMembershipMutationVariables = { teamId: string; data: MembershipCreateBody };

export const useCreateTeamMembership = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createTeamMembership>>,
    TError,
    CreateTeamMembershipMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createTeamMembership>>,
  TError,
  CreateTeamMembershipMutationVariables,
  TContext
> => {
  return useMutation(getCreateTeamMembershipMutationOptions(options));
};
export const getRemoveTeamMembershipUrl = (teamId: string, membershipId: string) => {
  return `/api/v1/teams/${teamId}/memberships/${membershipId}`;
};

export const removeTeamMembership = async (
  teamId: string,
  membershipId: string,
  membershipEndBody: MembershipEndBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<MembershipResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<MembershipResponseOutput>(
    getRemoveTeamMembershipUrl(teamId, membershipId),
    {
      ...options,
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
      body: JSON.stringify(membershipEndBody),
      schema: MembershipResponseOutput,
    },
  );
};

export const getRemoveTeamMembershipMutationKey = () => ['removeTeamMembership'] as const;

export const getRemoveTeamMembershipMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeTeamMembership>>,
    TError,
    RemoveTeamMembershipMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof removeTeamMembership>>,
  TError,
  RemoveTeamMembershipMutationVariables,
  TContext
> => {
  const mutationKey = getRemoveTeamMembershipMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof removeTeamMembership>>,
    RemoveTeamMembershipMutationVariables
  > = (props) => {
    const { teamId, membershipId, data } = props ?? {};

    return removeTeamMembership(teamId, membershipId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RemoveTeamMembershipMutationResult = NonNullable<
  Awaited<ReturnType<typeof removeTeamMembership>>
>;
export type RemoveTeamMembershipMutationBody = MembershipEndBody;
export type RemoveTeamMembershipMutationError = unknown;
export type RemoveTeamMembershipMutationVariables = {
  teamId: string;
  membershipId: string;
  data: MembershipEndBody;
};

export const useRemoveTeamMembership = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeTeamMembership>>,
    TError,
    RemoveTeamMembershipMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof removeTeamMembership>>,
  TError,
  RemoveTeamMembershipMutationVariables,
  TContext
> => {
  return useMutation(getRemoveTeamMembershipMutationOptions(options));
};
